/**
 * Dependency-injection seam for grid-machinery ODL dependencies.
 *
 * The generic grid components (EntityGrid, useGridSpec, columnRenderers) read
 * their ODL-specific dependencies from this context. ODL mounts
 * DataGridConfigProvider once at the app root with the full ODL config.
 * When no provider is present, GENERIC_DEFAULTS supply safe no-op or
 * identity fallbacks so grids render without crashing.
 *
 * Contains:
 * - GENERIC_DEFAULTS — safe fallback config object (every config slot)
 * - DataGridConfigContext — the React context (default = GENERIC_DEFAULTS)
 */

import { t } from 'i18next';
import React from 'react';

import { BASE_STAT_CARD_RENDERERS, resolveStatCardRenderer } from '@sophocles/data-grids/grid/statCardRenderers';

import { BASE_ACTION_ICON_REGISTRY, resolveActionIcon } from './actionIcons';

// ---------------------------------------------------------------------------
// Generic formatter defaults
// ---------------------------------------------------------------------------

/** Maps truthy/falsy to 'Yes'/'No'. Generic default for config.formatters.boolean. */
const genericBooleanFormatter = v => (v ? t('general.yes', 'Yes') : t('general.no', 'No'));

/**
 * Converts an ISO/JS date value to a locale string.
 * Generic default for config.formatters.datetime.
 */
const genericDatetimeFormatter = v => {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

// ---------------------------------------------------------------------------
// GENERIC_DEFAULTS
// ---------------------------------------------------------------------------

/**
 * Safe fallback values for every config slot. All non-null slots are no-ops,
 * identity functions, or minimal stubs so grids render correctly outside the
 * provider (tests, stories, or non-ODL contexts).
 *
 * fetchFn is null (guarded at the call site); EntityGrid returns empty rows
 * when no fetch is configured.
 *
 * usePreferenceStore is an in-memory stub (no persistence, always loaded).
 * EntityGrid's code path never exercises persistFilters, so this stub is only
 * relevant if a non-EntityGrid caller uses the config directly.
 */
export const GENERIC_DEFAULTS = {
  /** List-fetch function: (path, params) => Promise<{data, meta}>. Null → empty rows. */
  fetchFn: null,

  /**
   * Stats-fetch function: (path, params) => Promise<object>, for the aggregate
   * numbers behind a grid's descriptor stat cards. Null → the grid fetches no
   * stats of its own and shows only what a caller passes through.
   */
  fetchStatsFn: null,

  /**
   * Hook that resolves a grid-spec descriptor by path.
   * Generic: always returns unloaded-but-safe state (no server call).
   */
  useSpecQuery: (_path, _params) => ({ data: undefined, isLoading: false, isError: false }),

  /** Serializes grid params for the wire format. Generic: identity pass-through. */
  paramSerializer: p => p,

  /** Resolves a { type, id, name } reference to { label, route }. Generic: label from .name, no route. */
  resolveReference: ref => ({ label: ref?.name ?? '', route: null }),

  /**
   * Preference-store hook (same return signature as useUserPreference).
   * Generic: in-memory only, no persistence, always reports loaded.
   * EntityGrid's default path (persistFilters off) never exercises this.
   */
  usePreferenceStore: (_key, defaultValue) => [defaultValue, () => {}, true],

  /** i18n translator. Generic: returns fallback, or key when no fallback supplied. */
  translate: (key, fallback) => (fallback !== undefined ? fallback : key),

  formatters: {
    boolean: genericBooleanFormatter,
    datetime: genericDatetimeFormatter,
  },

  /** Resolves a BE-descriptor icon name to an MUI component. Generic: base registry only. */
  resolveActionIcon: name => resolveActionIcon(name, BASE_ACTION_ICON_REGISTRY),

  /**
   * Resolves a BE-descriptor stat-card type to the renderer that draws it.
   * Generic: base registry only, which falls back to the count renderer for a
   * type it has no entry for.
   */
  resolveStatCardRenderer: type => resolveStatCardRenderer(type, BASE_STAT_CARD_RENDERERS),
};

const DataGridConfigContext = React.createContext(GENERIC_DEFAULTS);

export default DataGridConfigContext;
